<template>
    <div class="dashboard-header" :class="{'dashboard-header__with-image': member.cover_url}" :style="coverUrl">
        <a href="profile/edit" class="dashboard-header__cover-edit"><i class="fa fa-camera"></i></a>
        <div class="dashboard-header__user-info">
            <div class="dashboard-header__avatar-container" v-if="member.avatar_url">
                <Avatar :src="member.avatar_url" :openToWork="member.open_to_work"></Avatar>
            </div>
            <h1 class="dashboard-header__name" v-if="member.name.length">
                {{member.name}}
            </h1>
            <h1 class="dashboard-header__name" v-else-if="requestError">
                Something's not working as expected...
            </h1>
            <h1 class="dashboard-header__name" v-else>
                Loading...
            </h1>
            <transition name="dashboard-header-transition">
                <div class="dashboard-header__stats" v-if="!loading">
                    <Statistic kind="Follower" :count="member.followers_count"></Statistic>,
                    <Statistic kind="Tutorial" :count="member.tutorials_count"></Statistic>,
                    <Statistic kind="Model" :count="member.models_count"></Statistic>,
                    <Statistic kind="Badge" :count="member.badges_count"> received</Statistic>
                </div>
            </transition>
            <transition name="dashboard-header-transition">
                <div class="dashboard-header__badges" v-if="!loading">
                    <Badge :image-url="badge.image_url" :url="badge.url" :title="badge.name" v-for="(badge, badgeIndex) in member.badges" :key="badgeIndex"></Badge>
                </div>
            </transition>
            <transition name="dashboard-header-transition">
                <h6 class="dashboard-header__more-badges" v-if="!loading && member.badges_to_earn && member.badges_to_earn.length">
                    {{member.badges_to_earn.length}} More Badges To Earn
                </h6>
            </transition>
            <transition name="dashboard-header-transition">
                <div class="dashboard-header__badges-to-earn" v-if="!loading && member.badges_to_earn && member.badges_to_earn.length">
                    <Badge :more="true" :image-url="badge.image_url" :url="badge.url" :title="badge.name" v-for="(badge, badgeIndex) in member.badges_to_earn" :key="badgeIndex"></Badge>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
  import Avatar from './header/Avatar'
  import Statistic from './header/Statistic'
  import Badge from './header/Badge'

  export default {
    name: 'Header',
    components: {
      Avatar,
      Statistic,
      Badge
    },
    props: {
      currentMember: {
        default() { return {} },
        required: true,
        type: Object
      },
      error: {
        required: false,
        type: Object,
        default () {
          return {
            message: null,
            link: null
          };
        }
      }
    },

    data () {
      return {
        loading: true,
        member: {
          name: '',
          avatar_url: window.Grabcad.defaultAvatar || null,
          cover_url: null,
          open_to_work: false,
          followers_count: 0,
          tutorials_count: 0,
          models_count: 0,
          badges_count: 0,
          badges: [],
          badges_to_earn: []
        }
      }
    },

    computed: {
      requestError () {
        return !!this.error.message && this.error.message.length > 0;
      },
      coverUrl () {
        return {
          '--cover-url': `url(${this.member.cover_url})`,
        }
      }
    },

    watch: {
      currentMember (newValue) {
        this.loading = false
        this.member = newValue
        this.member.avatar_url = newValue.avatar_url || window.Grabcad.defaultAvatar,
        this.member.cover_url = newValue.cover_url || null,
        this.member.badges_to_earn = newValue.badges_to_earn.filter(b => !b.archived)
      }
    }
  }
</script>

<style lang="scss" scoped>
    .dashboard-header {
        position: relative;
        display: block;
        width: 100%;
        padding-top: 80px;
        padding-bottom: 50px;

        &:before {
            position: absolute;
            top: 0;
            display: block;
            width: 100%;
            content: '';
            height: 250px;
            background: linear-gradient(0deg, #222222, #222222), #FFFFFF;
            z-index: 1;
        }

        &__with-image:before {
            background-position: center center;
            background-size: cover;
            background-image: var(--cover-url);
        }

        &__user-info {
            position: relative;
            z-index: 2;
            margin: 0 auto;
            padding: 0 23px 23px 23px;
            max-width: 540px;
            min-height: 130px;
            background: $card-background-color;
            border: 1px solid #DDDDDD;
            box-sizing: border-box;
            box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.0647928);
            border-radius: 4px;
        }

        &__avatar-container {
            width: 100%;
        }

        &__name {
            margin-top: 15px;
            font-style: normal;
            font-weight: bold;
            font-size: $dashboard-font-size-heading;
            line-height: 33px;
            text-align: center;
        }

        &__stats {
            margin-top: 15px;
            text-align: center;
            font-style: normal;
            font-weight: normal;
            font-size: $dashboard-font-size-regular;
            line-height: 16px;

            max-height: 48px; // Transition
            opacity: 1;
        }

        &__badges, &__badges-to-earn {
            margin-top: 15px;
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            justify-content: center;

            max-height: 350px; // Transition
            opacity: 1;

            .badge {
                margin-right: 5px;
                margin-left: 5px;
            }
        }

        &__more-badges {
            margin-top: 15px;
            font-style: normal;
            font-weight: normal;
            font-size: 10px;
            line-height: 14px;
            text-align: center;
            letter-spacing: 1px;
            text-transform: uppercase;

            max-height: 20px; // Transition
            opacity: 1;
        }


        &-transition-enter-active, &-transition-leave-active {
            transition: max-height 1s, opacity 1s;
        }

        &-transition-enter, &-transition-leave-to {
            max-height: 0;
            opacity: 0;
        }

        &__cover-edit {
            position: absolute;
            z-index: 50;
            right: 20px;
            top: 13px;
            text-shadow: 0 0 6px rgba(0, 0, 0, 0.50);
            color: #FFF;
            font-size: 22px;
            text-align: center;
            opacity: 0.5;
        }
    }
</style>
